import React, { useEffect, useRef, useState } from "react";
import { ActionMenu } from "./ActionMenu";
import { getTaskStatus, getTimelineStatusColor } from "../utils/helper";
import { createPortal } from "react-dom";
import { MilestoneIcon } from "../icons/icons";
import { dateOnlyFormatted } from "../../../utils/systemSettings";
import moment from "moment";
import { TimelineTaskIcons } from "../../../helpers/utils";

const taskStatusMapper = {
  Inprogress: "In Progress",
  Monitoring: "Monitoring",
  Slipping: "Slipping",
  Completed: "Completed",
  Onhold: "On Hold",
};

const getStatus = (status) => taskStatusMapper[status] ?? "-";

export function TaskBar({
  left,
  width,
  task,
  handleDragStart,
  handleAddDependency,
  handleTaskModal,
  handleMarkCompleted,
  handleDuplicateTask,
  setActivePopup,
  critical,
  isHideTask = false,
  isReadOnly = false,
}) {
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const dragStartRef = useRef(null);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenu && !menuRef.current?.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    const handleScroll = () => {
      if (activeMenu) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [activeMenu]);

  const handleTaskMenu = (e) => {
    e.stopPropagation();

    const buttonRect = menuButtonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const menuHeight = 150;
    const topPosition =
      buttonRect.bottom + menuHeight > viewportHeight
        ? buttonRect.top - menuHeight
        : buttonRect.bottom;

    setMenuPosition({
      top: topPosition,
      left: buttonRect.left,
    });

    setActiveMenu((prev) => (prev === task.id ? null : task.id));

    setActivePopup({
      isVisible: false,
      position: { x: 0, y: 0 },
      elementId: null,
    });
  };

  const handleMouseDown = (e) => {
    if (
      e.button === 2 ||
      e.target.closest(".task-menu") ||
      e.target.closest(".resize-handle-timeline")
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    dragStartRef.current = { x: e.clientX, y: e.clientY, dragging: false };
  };

  const handleMouseMove = (e) => {
    setTooltipPosition({ x: e.clientX, y: e.clientY });
    if (isReadOnly)
      return;

    if (!dragStartRef.current) return;

    const dx = Math.abs(e.clientX - dragStartRef.current.x);
    const dy = Math.abs(e.clientY - dragStartRef.current.y);

    if (!dragStartRef.current.dragging && (dx > 3 || dy > 3)) {
      dragStartRef.current.dragging = true;
      setIsDragging(true);
      handleDragStart(e, task.id, "move", task.name);
    }
  };

  const handleMouseUp = () => {
    dragStartRef.current = null;
    if (isReadOnly)
      return;
    setIsDragging(false);
  };

  const handleResizeStart = (e, type) => {
    if (isReadOnly)
      return;
    e.preventDefault();
    e.stopPropagation();
    handleDragStart(e, task.id, type, task.name);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleTaskMenu(e);
  };

  const addDependencyHandler = (e) => {
    if (isReadOnly)
      return;
    e.stopPropagation();
    setActiveMenu(null);
    handleAddDependency();
  };

  const handleTaskModalWithClose = (e) => {
    e.stopPropagation();
    setActiveMenu(null);
    handleTaskModal();
  };

  const handleMarkCompletedWithClose = (e) => {
    if (isReadOnly)
      return;
    e.stopPropagation();
    setActiveMenu(null);
    handleMarkCompleted();
  };

  const handleDuplicateTaskWithClose = (e) => {
    if (isReadOnly)
      return;
    e.stopPropagation();
    setActiveMenu(null);
    handleDuplicateTask();
  };

  const useTextWidth = (text, dependencies = []) => {
    const [width, setWidth] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
      if (ref.current) {
        setWidth(ref.current.getBoundingClientRect().width);
      }
    }, [text, ...dependencies]);

    return [ref, width];
  };

  const [textRef, textWidth] = useTextWidth(task.name, [task.name]);

  return (
    <div
      className="absolute top-1/2 -translate-y-1/2 h-6 flex flex-row items-center justify-between select-none z-1"
      style={{
        left: `${left - textWidth - 8}px`,
      }}
    >
      {/* Task Name Text */}
      <span
        ref={textRef}
        className={` text-s font-medium text-left whitespace-nowrap pr-2 pointer-events-none ${isHideTask && 'invisible'}`}
      >
        {task.name}
      </span>

      {/* Task Bar */}
      <div
        id={`task-bar-${task.id}`}
        className="relative"
        onMouseEnter={() => setTooltipVisible(true)}
        onMouseLeave={() => {
          dragStartRef.current = null;
          if (!isReadOnly) {
            setIsDragging(false);
          }
          setTooltipVisible(false);
        }}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onContextMenu={handleContextMenu}
        ref={menuButtonRef}
        onClick={(e) => {isReadOnly && handleTaskModalWithClose(e)}}
        style={{
          width: `${width}px`,
          cursor: isDragging ? "grabbing" : isReadOnly ? "pointer" : "grab",
        }}
      >
        {/* Left resize handle */}
        <div
          className={`resize-handle-timeline absolute left-0 top-0 w-3 h-full z-30 ${!isReadOnly ? 'cursor-ew-resize' : ''}`}
          onMouseDown={(e) => !isReadOnly && handleResizeStart(e, "start")}
        />

        {/* Task Bar Content */}
        <div
          className={`flex justify-between items-center rounded w-full px-1 h-[16px] ${getTimelineStatusColor(
            task.status,
            task.isCritical,
            critical
          )}`}
        >
          <div className="flex items-center justify-start gap-1 w-full overflow-hidden">
            {width > 20 && (
              <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center">
                {!critical
                  ? TimelineTaskIcons[task.status]()
                  : critical && task.isCritical
                    ? TimelineTaskIcons[2]()
                    : TimelineTaskIcons[task.status]("#A0A0A0")}
              </div>
            )}
            <span className="font-medium text-xs capitalize text-center truncate">
              {getStatus(getTaskStatus(task.status, task.isCritical, critical))}
            </span>
          </div>
        </div>

        {/* Right resize handle */}
        <div
          className={`resize-handle-timeline absolute right-0 top-0 w-3 h-full ${!isReadOnly ? 'cursor-ew-resize' : ''} z-30`}
          onMouseDown={(e) => {
            e.preventDefault();
            handleResizeStart(e, "end");
          }}
        />
      </div>

      {/* Milestone indicator */}
      {task?.isMilestone && (
        <div className="ml-1">
          <MilestoneIcon size={16} />
        </div>
      )}

      {/* End date */}
      {task?.endDate && (
        <span className="pl-1 text-xs whitespace-nowrap pointer-events-none">
          {moment(task?.endDate).format("DD-MM")}
        </span>
      )}

      {/* Tooltip */}
      {tooltipVisible &&
        createPortal(
          <div
            className="fixed z-[200] bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg pointer-events-none"
            style={{
              top: `${tooltipPosition.y + 10}px`,
              left: `${tooltipPosition.x + 10}px`,
              maxWidth: "200px",
              whiteSpace: "nowrap",
            }}
          >
            <div className="font-semibold">{task.name}</div>
            <div className="font-semibold">
              Start date: {dateOnlyFormatted(setting, task.startDate)}
            </div>
            <div className="font-semibold">
              End date: {dateOnlyFormatted(setting, task.endDate)}
            </div>
            <div className="capitalize">
              Status:{" "}
              {getStatus(getTaskStatus(task.status, task.isCritical, critical))}
            </div>
            <div>Dependencies: {task.dependencies?.length || 0}</div>
          </div>,
          document.body
        )}

      {/* Action Menu */}
      {activeMenu === task.id &&
        createPortal(
          <div
            ref={menuRef}
            className="task-menu absolute z-[100]"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              position: "fixed",
            }}
          >
            <ActionMenu
              handleMarkCompleted={handleMarkCompletedWithClose}
              handleDuplicateTask={handleDuplicateTaskWithClose}
              addDependencyHandler={addDependencyHandler}
              handleTaskModal={handleTaskModalWithClose}
              taskStatus={task.status}
              isReadOnly={isReadOnly}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
